import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { OrderEditDialog } from "@/pages/admin/OrderEditDialog";
import { api, brl, fmtDate, formatError, paymentStatusLabel, STATUSES } from "@/lib/api";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [ctype, setCtype] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () =>
    api.get("/admin/orders", { params: { status: status === "all" ? undefined : status, q: q || undefined, customer_type: ctype === "all" ? undefined : ctype } })
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(formatError(e)));

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, ctype]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Pedidos</h1>
        <p className="text-slate-500">Atualize o status de produção e avise o cliente quando o pedido estiver disponível para retirada.</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número, nome ou e-mail" className="h-11 bg-white pl-9" data-testid="admin-orders-search" />
        </div>
        <Select value={ctype} onValueChange={setCtype}>
          <SelectTrigger className="h-11 bg-white sm:w-48" data-testid="admin-orders-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os clientes</SelectItem>
            <SelectItem value="cliente" data-testid="filter-type-cliente">Clientes</SelectItem>
            <SelectItem value="revendedor" data-testid="filter-type-revendedor">Revendedores</SelectItem>
            <SelectItem value="loja" data-testid="filter-type-loja">Lojas</SelectItem>
            <SelectItem value="vip" data-testid="filter-type-vip">Clientes VIP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 bg-white sm:w-64" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {STATUSES.map((s, i) => <SelectItem key={s} value={s} data-testid={`filter-status-${i}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <Table data-testid="admin-orders-table">
          <TableHeader>
            <TableRow><TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Data</TableHead><TableHead>Total</TableHead><TableHead>Pagamento</TableHead><TableHead>Status</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} onClick={() => setSelected(o)} className="cursor-pointer" data-testid={`admin-order-row-${o.number}`}>
                <TableCell className="font-mono font-bold">#{o.number}</TableCell>
                <TableCell><p className="font-semibold">{o.customer.name}{o.customer_type === "revendedor" && <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-800" data-testid={`reseller-badge-${o.number}`}>Revendedor</span>}{o.customer_type === "loja" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800" data-testid={`store-badge-${o.number}`}>Loja</span>}{o.customer_type === "vip" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`vip-badge-${o.number}`}>VIP</span>}</p><p className="text-xs text-slate-500">{o.customer.email}</p></TableCell>
                <TableCell className="whitespace-nowrap text-slate-500">{fmtDate(o.created_at)}</TableCell>
                <TableCell className="font-mono">{brl(o.total)}</TableCell>
                <TableCell className="text-xs" data-testid={`admin-order-payment-${o.number}`}><span className={o.payment_status === "paid" ? "font-bold text-green-700" : "text-amber-700"}>{paymentStatusLabel(o.payment_status)}</span>{o.payment_method === "cash" && <span className="block text-slate-500">Dinheiro</span>}</TableCell>
                <TableCell><StatusBadge status={o.status} testId={`admin-order-status-${o.number}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {orders.length === 0 && <p className="p-10 text-center text-sm text-slate-500" data-testid="admin-no-orders">Nenhum pedido encontrado.</p>}
      </div>
      {selected && <OrderEditDialog order={selected} onClose={() => setSelected(null)} onSaved={(o) => { setSelected(o); load(); }} onDeleted={() => { setSelected(null); load(); }} />}
    </div>
  );
}
